<template>
    <div class="print-job">
        <print-loader
            :isMobileDevice="isMobileDevice"
            :loadingState="loadingState"
            @cancel="$emit('cancel')"
            @close="$_onStop()"
        >
        </print-loader>
        <print-feedback-gelderland
            v-if="isReady && isOrgGelderlandAndKavelAndTerreinSelected"
            :displayObjectPrimary="kavelObject"
            :displayObjectSecondary="terreinObject"
            :qrCodeUrl="gelderlandTemplateQrCodeUrl"
            :panelTitle="panelTitle"
            @mapReady="$_onMapReady()"
            @imageReady="$_onImageReady()"
        >
        </print-feedback-gelderland>
        <print-feedback
            v-if="isReady && !isOrgGelderlandAndKavelAndTerreinSelected"
            :displayObject="selectedObject"
            :panelTitle="panelTitle"
            :isFeedBack="isFeedBack"
            @mapReady="$_onMapReady()"
            @imageReady="$_onImageReady()"
        >
        </print-feedback>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { NG_LAYER_TYPES } from '../../../constants';
    import { PAPER_FORMATS } from '../../../constants/print';
    import { isMobileDevice } from '../../../services/utilities/devices';
    import FeedbackPrint from './FeedbackPrint.vue';
    import FeedbackPrintGelderland from './FeedbackPrintGelderland.vue';
    import PrintLoader from './../PrintLoader.vue';

    export default {
        name: 'FeedbackPrintExecuter',
        components: {
            'print-feedback': FeedbackPrint,
            'print-feedback-gelderland': FeedbackPrintGelderland,
            'print-loader': PrintLoader
        },
        data() {
            return {
                loadingState: {
                    tiles: null,
                    message: null,
                    stage: 1,
                    errors: []
                },
                mapReady: false,
                imageReady: false,
                isPrinting: false,
                isMobileDevice: isMobileDevice(),
                gelderlandTemplateOrgId: null,
                gelderlandTemplateQrCodeUrl: null,
                gelderlandTemplateLayerKavels: null,
                gelderlandTemplateLayerTerreinen: null,
                isReady: false
            };
        },
        async created() {
            if (!this.getReportPrintJob) {
                this.$_onStop();
            }
            this.$store.commit(
                'SET_PAGE_FEEDBACK_PRINT_SIZE_STYLE',
                PAPER_FORMATS.A4_PORTRAIT
            );

            // Get appsettings for Gelderland
            let appSetting = await this.$store.dispatch('useApiData', {
                name: 'GetGelderlandTemplateSettings'
            });
            this.gelderlandTemplateOrgId =
                appSetting?.data?.GelderlandTemplateOrganizationId ?? null;
            this.gelderlandTemplateQrCodeUrl =
                appSetting?.data?.GelderlandTemplateQrCodeUrl ?? null;
            this.gelderlandTemplateLayerKavels = this.isMobile
                ? appSetting?.data?.GelderlandTemplateLayerKavels ?? null
                : appSetting?.data?.GelderlandTemplateLayerKavelsWeb ?? null;
            this.gelderlandTemplateLayerTerreinen = this.isMobile
                ? appSetting?.data?.GelderlandTemplateLayerTerreinen ?? null
                : appSetting?.data?.GelderlandTemplateLayerTerreinenWeb ?? null;

            this.isReady = true;
        },
        methods: {
            $_onStop() {
                this.$router.push({ name: 'ngMap' });
            },
            $_onMapReady() {
                this.mapReady = true;
            },
            $_onImageReady() {
                this.imageReady = true;
            },
            $_onPrint() {
                // we can print if:
                // - the loading state is inactive (false) and
                // - the tiles have been loaded (mapReady) and
                // - the optional image is loaded (imageReady)
                if (
                    !this.getLoadingState &&
                    this.mapReady &&
                    this.imageReady &&
                    !this.isPrinting
                ) {
                    this.isPrinting = true;
                    this.loadingState.stage = 2;
                    window.print();
                    this.loadingState.stage = 3;
                    if (!this.isMobileDevice) {
                        this.$_onStop();
                    }
                }
            }
        },
        computed: {
            ...mapGetters([
                'getReportPrintJob',
                'getLoadingState',
                'getOrganizationId',
                'isMobile'
            ]),
            selectedObject() {
                return this.getReportPrintJob.selectedObject;
            },
            selectionObjects() {
                return this.getReportPrintJob.selectionObjects;
            },
            isFeedBack() {
                return this.getReportPrintJob.isFeedBack;
            },
            panelTitle() {
                return this.getReportPrintJob.title;
            },
            kavelObject() {
                return this.selectedObject?.layerTitle ===
                    this.gelderlandTemplateLayerKavels &&
                    this.selectedObject?.type === NG_LAYER_TYPES.THEME
                    ? this.selectedObject
                    : null;
            },
            terreinObject() {
                const terreinId = this.selectedObject?.info.find(
                    (i) => i.attribute === 'terreinid'
                )?.displayValue;
                return this.selectionObjects?.find(
                    (i) =>
                        i.layerTitle === this.gelderlandTemplateLayerTerreinen &&
                        i.info.some(
                            (info) =>
                                info.attribute === 'ibis_id' &&
                                info.displayValue === terreinId
                        )
                );
            },
            isOrgGelderlandAndKavelAndTerreinSelected() {
                return (
                    !!this.kavelObject &&
                    !!this.terreinObject &&
                    this.getOrganizationId === Number(this.gelderlandTemplateOrgId)
                );
            }
        },
        watch: {
            getLoadingState: {
                handler: function (newVal) {
                    if (!newVal) {
                        // set a delay so wfsrequests can be queued
                        setTimeout(this.$_onPrint, 300);
                    }
                },
                immediate: true
            },
            mapReady: {
                handler: function () {
                    this.$_onPrint();
                },
                immediate: true
            },
            imageReady: {
                handler: function () {
                    this.$_onPrint();
                },
                immediate: true
            }
        }
    };
</script>

<style scoped lang="scss">
.print-job {
    margin: 0;
    padding: 0;
}
@media screen {
    .print-job {
        position: relative;
        overflow: hidden;
        width: 100%;
        height: 100vh;
    }
}
</style>
